import { useState } from 'react';
import {
    Box, Stack, Typography, Button, IconButton, Tooltip, Select, MenuItem, Chip
} from '@mui/material';
import CommentIcon from '@mui/icons-material/Comment';
import ClearIcon from '@mui/icons-material/Clear';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const STARTER_CHORES = ['Cooking', 'Dishes', 'Laundry', 'Groceries', 'Trash'];

const ChoresSection = ({
    chores,
    myChoreWeight,
    partnerChoreWeight,
    relationshipName,
    onAddChore,
    onChoreChange,
    onResetChore,
    onOpenComment,
    onRemoveComment,
    onAddStarterChores,
    reorderMode,
    onToggleReorder,
    onMoveChore,
    onSaveOrder,
}) => {
    const [selectedStarters, setSelectedStarters] = useState(new Set(STARTER_CHORES));
    const [addingStarters, setAddingStarters] = useState(false);
    const [dragIdx, setDragIdx] = useState(null);
    const [dragOverIdx, setDragOverIdx] = useState(null);

    const toggleStarter = (name) => {
        setSelectedStarters(prev => {
            const next = new Set(prev);
            next.has(name) ? next.delete(name) : next.add(name);
            return next;
        });
    };

    const handleAddStarters = async () => {
        if (selectedStarters.size === 0 || !onAddStarterChores) return;
        setAddingStarters(true);
        try {
            await onAddStarterChores([...selectedStarters]);
        } finally {
            setAddingStarters(false);
        }
    };
    const partnerFirstName = relationshipName?.split(' ')[0] || 'Partner';

    return (
        <Box sx={{ flex: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ marginBottom: 2 }}>
                <Box>
                    <Typography variant="h5" sx={{ display: 'inline-block' }}>
                        Chores
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                        <Box sx={{ px: 1, py: 0.25, borderRadius: 1, backgroundColor: 'rgba(123, 107, 141, 0.12)', border: '1px solid #7B6B8D' }}>
                            <Typography variant="caption" sx={{ color: '#7B6B8D', fontWeight: 600 }}>
                                Me: {myChoreWeight}
                            </Typography>
                        </Box>
                        <Box sx={{ px: 1, py: 0.25, borderRadius: 1, backgroundColor: 'rgba(196, 133, 106, 0.12)', border: '1px solid #C4856A' }}>
                            <Typography variant="caption" sx={{ color: '#C4856A', fontWeight: 600 }}>
                                {partnerFirstName}: {partnerChoreWeight}
                            </Typography>
                        </Box>
                    </Stack>
                </Box>
                <Stack direction="row" spacing={1}>
                    {reorderMode ? (
                        <Button
                            variant="contained"
                            size="small"
                            onClick={onSaveOrder}
                            sx={{ borderRadius: 2, background: '#7B6B8D', '&:hover': { background: '#6A5A7A' } }}
                        >
                            Done
                        </Button>
                    ) : (
                        <>
                            {chores.length > 1 && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={onToggleReorder}
                                    sx={{ borderRadius: 2, borderColor: '#7B6B8D', color: '#7B6B8D', '&:hover': { borderColor: '#6A5A7A', color: '#6A5A7A', backgroundColor: 'rgba(123,107,141,0.06)' } }}
                                >
                                    Reorder
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                size="small"
                                onClick={onAddChore}
                                sx={{ borderRadius: 2, background: '#7B6B8D', '&:hover': { background: '#6A5A7A' } }}
                            >
                                Add Chore
                            </Button>
                        </>
                    )}
                </Stack>
            </Stack>

            {chores.length > 0 ? (
                reorderMode ? (
                    chores.map((chore, index) => (
                        <Box
                            key={chore.chore_id}
                            draggable
                            onDragStart={() => setDragIdx(index)}
                            onDragOver={(e) => { e.preventDefault(); setDragOverIdx(index); }}
                            onDrop={() => {
                                if (dragIdx !== null && dragIdx !== index) {
                                    const diff = index - dragIdx;
                                    const step = diff > 0 ? 1 : -1;
                                    for (let i = 0; i < Math.abs(diff); i++) {
                                        onMoveChore(dragIdx + i * step, step);
                                    }
                                }
                                setDragIdx(null);
                                setDragOverIdx(null);
                            }}
                            onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
                            sx={{
                                mb: 1, p: 1.5, borderRadius: 2,
                                border: dragOverIdx === index ? '2px solid #7B6B8D' : '1px solid #E8E8E8',
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                backgroundColor: dragIdx === index ? 'rgba(123,107,141,0.08)' : '#fff',
                                cursor: 'grab', userSelect: 'none',
                                transition: 'border-color 0.15s, background-color 0.15s',
                                '&:active': { cursor: 'grabbing' },
                            }}
                        >
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <DragIndicatorIcon sx={{ color: '#bbb', fontSize: 20 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333' }}>
                                    {chore.name}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={0.5}>
                                <IconButton
                                    size="small"
                                    onClick={() => onMoveChore(index, -1)}
                                    disabled={index === 0}
                                    sx={{ color: '#7B6B8D' }}
                                >
                                    <KeyboardArrowUpIcon />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={() => onMoveChore(index, 1)}
                                    disabled={index === chores.length - 1}
                                    sx={{ color: '#7B6B8D' }}
                                >
                                    <KeyboardArrowDownIcon />
                                </IconButton>
                            </Stack>
                        </Box>
                    ))
                ) : (
                chores.map((chore) => (
                    <Box key={chore.chore_id} sx={{ marginBottom: 2 }}>
                        {chore.chore_type === 0 ? (
                            <Box sx={{
                                p: 2, borderRadius: 2, border: '1px solid',
                                borderColor: chore.value ? '#7B6B8D' : '#E8E8E8',
                                backgroundColor: chore.value ? 'rgba(184, 167, 212, 0.04)' : 'transparent',
                                transition: 'all 0.2s ease'
                            }}>
                                <Stack spacing={2}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333' }}>
                                            {chore.name}
                                        </Typography>
                                        <Stack direction="row" spacing={1}>
                                            <Tooltip title={chore.value ? 'Add/Edit Comment' : 'Select a value first'}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onOpenComment(chore)}
                                                        disabled={!chore.value}
                                                        sx={{ color: chore.comments ? '#7B6B8D' : '#999', '&:hover': { backgroundColor: 'rgba(184, 167, 212, 0.1)' } }}
                                                    >
                                                        <CommentIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            {chore.value && (
                                                <Tooltip title="Reset chore">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onResetChore(chore.chore_id)}
                                                        sx={{ color: '#C4856A', '&:hover': { backgroundColor: 'rgba(244, 181, 196, 0.1)' } }}
                                                    >
                                                        <ClearIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Stack>
                                    </Stack>

                                    <Stack direction="row" spacing={2}>
                                        <Button
                                            fullWidth
                                            variant={chore.value === 'me' ? 'contained' : 'outlined'}
                                            onClick={() => onChoreChange(chore.chore_id, 'me')}
                                            sx={{
                                                borderRadius: 2, py: 1.2, borderColor: '#7B6B8D',
                                                color: chore.value === 'me' ? 'white' : '#7B6B8D',
                                                background: chore.value === 'me' ? '#7B6B8D' : 'transparent',
                                                '&:hover': { borderColor: '#6A5A7A', background: chore.value === 'me' ? '#6A5A7A' : 'rgba(184, 167, 212, 0.08)' }
                                            }}
                                        >
                                            Me
                                        </Button>
                                        <Button
                                            fullWidth
                                            variant={chore.value === 'partner' ? 'contained' : 'outlined'}
                                            onClick={() => onChoreChange(chore.chore_id, 'partner')}
                                            sx={{
                                                borderRadius: 2, py: 1.2, borderColor: '#C4856A',
                                                color: chore.value === 'partner' ? 'white' : '#C4856A',
                                                background: chore.value === 'partner' ? '#C4856A' : 'transparent',
                                                '&:hover': { borderColor: '#B0745A', background: chore.value === 'partner' ? '#B0745A' : 'rgba(244, 181, 196, 0.08)' }
                                            }}
                                        >
                                            {partnerFirstName}
                                        </Button>
                                    </Stack>

                                    {chore.comments && (
                                        <Box sx={{ px: 1.5, py: 0.75, borderRadius: 1.5, backgroundColor: 'rgba(184, 167, 212, 0.08)', display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: '#555', flex: 1, fontSize: '0.8rem' }}>
                                                {chore.comments}
                                            </Typography>
                                            <Tooltip title="Remove comment">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onRemoveComment('chore', chore.chore_id)}
                                                    sx={{ color: '#bbb', p: 0.25, flexShrink: 0, '&:hover': { color: '#C4856A', backgroundColor: 'transparent' } }}
                                                >
                                                    <ClearIcon sx={{ fontSize: '0.85rem' }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    )}
                                </Stack>
                            </Box>
                        ) : chore.chore_type === 2 ? (
                            <Box sx={{
                                p: 2, borderRadius: 2, border: '1px solid',
                                borderColor: (chore.value?.me > 0 || chore.value?.partner > 0) ? '#7B6B8D' : '#E8E8E8',
                                backgroundColor: (chore.value?.me > 0 || chore.value?.partner > 0) ? 'rgba(184, 167, 212, 0.04)' : 'transparent',
                                transition: 'all 0.2s ease'
                            }}>
                                <Stack spacing={2}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333' }}>
                                            {chore.name}
                                        </Typography>
                                        <Stack direction="row" spacing={1}>
                                            <Tooltip title={(chore.value?.me > 0 || chore.value?.partner > 0) ? 'Add/Edit Comment' : 'Set a value first'}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onOpenComment(chore)}
                                                        disabled={!(chore.value?.me > 0 || chore.value?.partner > 0)}
                                                        sx={{ color: chore.comments ? '#7B6B8D' : '#999', '&:hover': { backgroundColor: 'rgba(184, 167, 212, 0.1)' } }}
                                                    >
                                                        <CommentIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            {(chore.value?.me > 0 || chore.value?.partner > 0) && (
                                                <Tooltip title="Reset chore">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onResetChore(chore.chore_id)}
                                                        sx={{ color: '#C4856A', '&:hover': { backgroundColor: 'rgba(244, 181, 196, 0.1)' } }}
                                                    >
                                                        <ClearIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Stack>
                                    </Stack>

                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>Me</Typography>
                                            <Select
                                                fullWidth
                                                value={chore.value?.me || 0}
                                                onChange={(e) => onChoreChange(chore.chore_id, { ...chore.value, me: e.target.value })}
                                                sx={{ borderRadius: 2, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#7B6B8D' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#6A5A7A' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#7B6B8D' } }}
                                            >
                                                <MenuItem value={0}>0</MenuItem>
                                                {[...Array(10).keys()].map((num) => (
                                                    <MenuItem key={`me-${num + 1}`} value={num + 1}>{num + 1}</MenuItem>
                                                ))}
                                            </Select>
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>{partnerFirstName}</Typography>
                                            <Select
                                                fullWidth
                                                value={chore.value?.partner || 0}
                                                onChange={(e) => onChoreChange(chore.chore_id, { ...chore.value, partner: e.target.value })}
                                                sx={{ borderRadius: 2, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#C4856A' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#B0745A' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#C4856A' } }}
                                            >
                                                <MenuItem value={0}>0</MenuItem>
                                                {[...Array(10).keys()].map((num) => (
                                                    <MenuItem key={`partner-${num + 1}`} value={num + 1}>{num + 1}</MenuItem>
                                                ))}
                                            </Select>
                                        </Box>
                                    </Stack>

                                    {chore.comments && (
                                        <Box sx={{ px: 1.5, py: 0.75, borderRadius: 1.5, backgroundColor: 'rgba(184, 167, 212, 0.08)', display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: '#555', flex: 1, fontSize: '0.8rem' }}>
                                                {chore.comments}
                                            </Typography>
                                            <Tooltip title="Remove comment">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onRemoveComment('chore', chore.chore_id)}
                                                    sx={{ color: '#bbb', p: 0.25, flexShrink: 0, '&:hover': { color: '#C4856A', backgroundColor: 'transparent' } }}
                                                >
                                                    <ClearIcon sx={{ fontSize: '0.85rem' }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    )}
                                </Stack>
                            </Box>
                        ) : null}
                    </Box>
                ))
                )
            ) : (
                <Box>
                    <Typography variant="body2" sx={{ color: '#888', mb: 1.5 }}>
                        No chores yet. Pick some common ones to get started:
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
                        {STARTER_CHORES.map(name => (
                            <Chip
                                key={name}
                                label={name}
                                onClick={() => toggleStarter(name)}
                                variant={selectedStarters.has(name) ? 'filled' : 'outlined'}
                                sx={{
                                    cursor: 'pointer',
                                    backgroundColor: selectedStarters.has(name) ? 'rgba(123,107,141,0.15)' : 'transparent',
                                    borderColor: '#7B6B8D',
                                    color: '#7B6B8D',
                                    fontWeight: selectedStarters.has(name) ? 600 : 400,
                                }}
                            />
                        ))}
                    </Stack>
                    <Button
                        variant="contained"
                        size="small"
                        disabled={selectedStarters.size === 0 || addingStarters}
                        onClick={handleAddStarters}
                        sx={{ borderRadius: 2, background: '#7B6B8D', '&:hover': { background: '#6A5A7A' } }}
                    >
                        {addingStarters ? 'Adding…' : `Add ${selectedStarters.size} chore${selectedStarters.size !== 1 ? 's' : ''}`}
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default ChoresSection;
